import { FC, useEffect, useState } from "react";

import { Stack } from "@mui/material";

import { BidderOtpCodeInput } from "./BidderOtpCodeInput";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Mail } from "@/icons/outlined";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

interface BidderOtpDialogProps {
  open: boolean;
  email: string;
  isVerifying: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<{ isValid: boolean }>;
  onResend: () => Promise<void>;
  sendErrorMessage?: string | null;
  inline?: boolean;
}

export const BidderOtpDialog: FC<BidderOtpDialogProps> = ({
  open,
  email,
  isVerifying,
  onClose,
  onVerify,
  onResend,
  sendErrorMessage,
  inline,
}) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const [code, setCode] = useState("");
  const [hasError, setHasError] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!open) {
      setCode("");
      setHasError(false);
      setCooldown(0);
    }
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldown((current) => current - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async (submittedCode: string) => {
    setHasError(false);
    const { isValid } = await onVerify(submittedCode);
    if (!isValid) {
      setHasError(true);
      setCode("");
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) {
      return;
    }
    setCode("");
    setHasError(false);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    await onResend();
  };

  const handleChange = (value: string) => {
    setCode(value);
    if (hasError) {
      setHasError(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      inline={inline}
      size="xs"
      icon={<Mail />}
      variant={isSmallScreen ? "drawer" : "default"}
      title={<span data-test="bidder-otp-dialog-title">{t("common", "bidderOtpDialog.title")}</span>}
      actions={[
        <FormButton
          key="verify"
          variant="solid"
          onClick={() => handleVerify(code)}
          disabled={code.length !== CODE_LENGTH || isVerifying}
          data-test="bidder-otp-dialog-verify"
        >
          {t("common", "bidderOtpDialog.verify")}
        </FormButton>,
        <FormButton key="cancel" variant="outline" onClick={onClose} data-test="bidder-otp-dialog-cancel">
          {t("common", "bidderOtpDialog.cancel")}
        </FormButton>,
      ]}
    >
      <FormTypography variant="body2">{t("common", "bidderOtpDialog.body", { email })}</FormTypography>
      <BidderOtpCodeInput code={code} setCode={handleChange} onComplete={handleVerify} disabled={isVerifying} />
      {hasError && (
        <FormTypography variant="body2" vibe="error" role="alert" data-test="bidder-otp-dialog-error">
          {t("common", "bidderOtpDialog.errorInvalidCode")}
        </FormTypography>
      )}
      {Boolean(sendErrorMessage) && (
        <FormTypography variant="body2" vibe="error" role="alert" data-test="bidder-otp-dialog-send-error">
          {sendErrorMessage}
        </FormTypography>
      )}
      <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
        <FormTypography variant="body2" vibe="moderate">
          {t("common", "bidderOtpDialog.didNotReceive")}
        </FormTypography>
        <FormButton variant="ghost" onClick={handleResend} disabled={cooldown > 0} data-test="bidder-otp-dialog-resend">
          {cooldown > 0
            ? t("common", "bidderOtpDialog.resendIn", { seconds: cooldown })
            : t("common", "bidderOtpDialog.resend")}
        </FormButton>
      </Stack>
    </FormDialog>
  );
};
